import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Spin,
  Form,
  Collapse,
  Button,
  theme,
  Checkbox,
  message,
  List,
  Tabs,
  Switch,
  Input,
  Empty,
  Tooltip,
  Tag,
  Modal,
  Radio,
} from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import {
  SearchOutlined,
  SettingOutlined,
  EditOutlined,
  CopyOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  TeamOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import {
  getConnectedGoogleAccounts,
  GetGoogleAccountResponse,
  ConnectedGoogleAccount,
  connectNewGoogleAccount,
  ConnectNewGoogleAccountResponse,
  disconnectGoogleAccount,
  getGoogleCalendarEvents,
  GoogleCalendarEventResponse,
  GoogleCalendarEvent,
  GoogleCalendarNotetakerInvitationState,
  ScheduledMeetingRecurrenceScope,
  updateFutureMeetings,
  updateGoogleCalendars,
  AttendedMeeting,
  getAttendedMeeting,
  getAttendedMeetings,
  GetAttendedMeetingsResponse,
  generateRecordingLink,
  GenerateRecordingLinkResponse,
  getDocument,
  GetDocumentResponse,
  cancelDocumentRequest,
  updateDocumentContent,
  startImmediateMeeting,
  syncGoogleCalendarEvents,
  startScheduledMeetingNow,
  isAttendedMeeting,
  getInvestors,
  getMembers,
  InvestorProfile,
  MembersData,
  MeetingAttendee,
  StartScheduledMeetingNowResponse,
} from '@/api/custom';
import { removeSpacesAndStrs } from '@wavvest/shared-utils';
import ReactMarkdown from 'react-markdown';
import GoogleCalendarIcon from '@/assets/logo/GoogleCalendarIcon.svg';
import { NotesIcon } from '@/assets/antdIcon';
import { getGoogleCalendarAccountDisplayName } from './account-display';
import { trackMeetingNotesUsage } from './usage-events';
import './index.scss';

type LeftPanelTabKey = 'upcoming' | 'past' | 'settings';
type MeetingHubArtifactType = 'summary' | 'transcript' | 'recording';

type MeetingAttendeeDisplay = {
  key: string;
  label: string;
  investorId?: string;
  phoneHref?: string;
};

const artifactPanelKeys = ['notes', 'transcript'];
const leftPanelTabKeys: LeftPanelTabKey[] = ['upcoming', 'past', 'settings'];
const meetingTargetLookupLimit = 64;
const meetingTargetLookupMaxPages = 10;

type CalendarNotetakerStatus = NonNullable<GoogleCalendarEvent['notetaker_status']>;
type RefreshLeftPanelOptions = {
  showLoading?: boolean;
};

const notetakerStatusCopy: Record<
  CalendarNotetakerStatus,
  {
    label: string;
    color: string;
    hint?: string;
  }
> = {
  scheduled: {
    label: 'Notetaker scheduled',
    color: 'blue',
  },
  join_scheduled: {
    label: 'Notetaker invited',
    color: 'blue',
  },
  starting: {
    label: 'Notetaker starting',
    color: 'processing',
    hint: 'Wavvest Note Taker is starting its browser session.',
  },
  signing_in: {
    label: 'Signing in',
    color: 'processing',
    hint: 'Wavvest Note Taker is signing in through the bot workspace.',
  },
  joining: {
    label: 'Joining',
    color: 'processing',
    hint: 'Wavvest Note Taker is opening Google Meet.',
  },
  waiting_for_admission: {
    label: 'Waiting for host',
    color: 'warning',
    hint: 'Admit Wavvest Note Taker in Google Meet.',
  },
  waiting_for_host: {
    label: 'Waiting for host',
    color: 'warning',
    hint: 'Admit Wavvest Note Taker in Google Meet.',
  },
  recording: {
    label: 'Recording',
    color: 'success',
  },
  processing: {
    label: 'Processing notes',
    color: 'processing',
  },
  completed: {
    label: 'Complete',
    color: 'default',
  },
  notes_ready: {
    label: 'Notes ready',
    color: 'success',
  },
  needs_reconnect: {
    label: 'Needs calendar reconnect',
    color: 'warning',
    hint: 'Reconnect Google Calendar so Wavvest can add the notetaker to this event.',
  },
  blocked_by_meeting_policy: {
    label: 'Meeting settings blocked notetaker',
    color: 'error',
    hint: 'The meeting settings prevented Wavvest Note Taker from joining.',
  },
  blocked_by_google_risk: {
    label: 'Google blocked notetaker',
    color: 'error',
    hint: 'Google blocked the notetaker before it could join. Wavvest support needs to review the bot account.',
  },
  captcha_or_account_challenge: {
    label: 'Google account challenge',
    color: 'error',
    hint: 'Google requires an account check before the notetaker can join again.',
  },
  host_denied: {
    label: 'Host denied',
    color: 'error',
    hint: 'The host denied Wavvest Note Taker in Google Meet.',
  },
  failed: {
    label: 'Notetaker failed',
    color: 'error',
  },
  disabled: {
    label: 'Notetaker off',
    color: 'default',
  },
};

const notetakerInvitationCopy: Record<
  GoogleCalendarNotetakerInvitationState,
  {
    label: string;
    hint?: string;
  }
> = {
  not_configured: {
    label: 'Notetaker setup pending',
    hint: 'No Meet bot account is configured for this workspace yet.',
  },
  not_required: {
    label: 'Notetaker not required',
  },
  pending: {
    label: 'Inviting notetaker',
    hint: 'Wavvest is adding the assigned bot to this calendar event.',
  },
  invited: {
    label: 'Notetaker invited',
    hint: 'The assigned bot is on this calendar event.',
  },
  failed: {
    label: 'Notetaker invite failed',
    hint: 'Wavvest could not add the assigned bot to this calendar event.',
  },
  missing_calendar_scope: {
    label: 'Calendar reconnect needed',
    hint: 'Reconnect Google Calendar so Wavvest can add the notetaker to this event.',
  },
  event_not_editable: {
    label: 'Notetaker not invited',
    hint: 'This calendar event cannot be edited from the connected Google account.',
  },
  event_not_found: {
    label: 'Calendar event not found',
    hint: 'Wavvest could not find this Google Calendar event during sync.',
  },
};

const disabledNotetakerCleanupCopy = {
  label: 'Notetaker off; calendar invite could not be removed',
  hint: 'The notetaker will not launch, but Google Calendar may still show the bot as invited.',
};

const calendarInviteCleanupFailureStates = new Set<GoogleCalendarNotetakerInvitationState>([
  'failed',
  'missing_calendar_scope',
  'event_not_editable',
  'event_not_found',
]);

const unknownNotetakerInvitationCopy = {
  label: 'Notetaker invite status unknown',
  hint: 'Refresh calendar sync to update this meeting.',
};

type MeetingMemoryStatus = NonNullable<AttendedMeeting['meeting_memory_status']>;

const meetingMemoryStatusCopy: Partial<
  Record<
    MeetingMemoryStatus,
    {
      label: string;
      hint: string;
      color: string;
    }
  >
> = {
  bound: {
    label: 'Profile Memory linked',
    hint: 'Meeting notes were matched to a client profile.',
    color: 'success',
  },
  client_selection_needed: {
    label: 'Profile Memory needs client',
    hint: 'A client could not be identified from the meeting attendees.',
    color: 'warning',
  },
  ambiguous_client_match: {
    label: 'Profile Memory needs client',
    hint: 'More than one client matched the meeting attendees.',
    color: 'warning',
  },
  client_not_authorized: {
    label: 'Profile Memory not linked',
    hint: 'This advisor cannot write meeting memory for the selected client.',
    color: 'error',
  },
  binding_failed: {
    label: 'Profile Memory status unavailable',
    hint: 'Wavvest could not check the client match for this meeting.',
    color: 'error',
  },
};

const getNotetakerInvitationCopy = (
  event: GoogleCalendarEvent,
): {
  copy: { label: string; hint?: string };
  classNameState: string;
} => {
  const invitationState = event.notetaker_invitation_state;
  if (
    event.status === 'UserDisabled' &&
    invitationState &&
    calendarInviteCleanupFailureStates.has(invitationState)
  ) {
    return {
      copy: disabledNotetakerCleanupCopy,
      classNameState: 'calendar_cleanup_failed',
    };
  }

  if (!invitationState) {
    return { copy: unknownNotetakerInvitationCopy, classNameState: 'unknown' };
  }

  if (Object.prototype.hasOwnProperty.call(notetakerInvitationCopy, invitationState)) {
    return {
      copy: notetakerInvitationCopy[invitationState as GoogleCalendarNotetakerInvitationState],
      classNameState: invitationState,
    };
  }

  return { copy: unknownNotetakerInvitationCopy, classNameState: 'unknown' };
};

const startableNotetakerStatuses: CalendarNotetakerStatus[] = ['scheduled'];

const isLeftPanelTabKey = (key: string): key is LeftPanelTabKey =>
  leftPanelTabKeys.includes(key as LeftPanelTabKey);

type MeetingHubTarget = {
  artifact: MeetingHubArtifactType | null;
  meetingJobId: string | null;
  summaryFileKey: string | null;
  transcriptFileKey: string | null;
};

type PendingNotetakerScopeChange = {
  event: GoogleCalendarEvent;
  checked: boolean;
};

const isMeetingHubArtifactType = (value: string | null): value is MeetingHubArtifactType =>
  value === 'summary' || value === 'transcript' || value === 'recording';

const meetingHubTargetFromSearchParams = (
  searchParams: URLSearchParams,
): MeetingHubTarget | null => {
  const artifact = searchParams.get('artifact');
  const target: MeetingHubTarget = {
    artifact: isMeetingHubArtifactType(artifact) ? artifact : null,
    meetingJobId: searchParams.get('meetingJobId'),
    summaryFileKey: searchParams.get('summaryFileKey'),
    transcriptFileKey: searchParams.get('transcriptFileKey'),
  };

  return target.artifact || target.meetingJobId || target.summaryFileKey || target.transcriptFileKey
    ? target
    : null;
};

const initialLeftPanelTab = (searchParams: URLSearchParams): LeftPanelTabKey =>
  searchParams.get('tab') === 'past' || meetingHubTargetFromSearchParams(searchParams)
    ? 'past'
    : 'upcoming';

const pastPanelKeyForArtifact = (artifact: MeetingHubArtifactType | null): string | null => {
  if (artifact === 'summary') return 'notes';
  if (artifact === 'transcript') return 'transcript';

  return null;
};

const defaultPastOpenPanelKeys = (artifact: MeetingHubArtifactType | null): string[] => {
  const artifactPanelKey = pastPanelKeyForArtifact(artifact);
  return artifactPanelKey ? [artifactPanelKey] : [...artifactPanelKeys];
};

const collapseKeysFromChange = (keys: string | string[]): string[] => {
  if (Array.isArray(keys)) return keys;
  return keys ? [keys] : [];
};

const renderCollapseIcon = ({ isActive }: { isActive?: boolean }) => (
  <Tooltip title={isActive ? 'Minimize section' : 'Expand section'} placement="left">
    <span className="meeting-hub-collapse-icon" aria-hidden="true">
      {isActive ? <MinusCircleOutlined /> : <PlusCircleOutlined />}
    </span>
  </Tooltip>
);

const meetingMatchesTarget = (meeting: AttendedMeeting, target: MeetingHubTarget): boolean =>
  meeting.job_id === target.meetingJobId ||
  (target.summaryFileKey !== null && meeting.summary_file_key === target.summaryFileKey) ||
  (target.transcriptFileKey !== null && meeting.transcript_file_key === target.transcriptFileKey);

const calendarNotetakerStatus = (event: GoogleCalendarEvent): CalendarNotetakerStatus =>
  event.notetaker_status ?? (event.status === 'UserDisabled' ? 'disabled' : 'scheduled');

const isActiveCalendarEvent = (event: GoogleCalendarEvent, now = new Date()): boolean => {
  const startTime = new Date(event.start_time).getTime();
  const endTime = new Date(event.end_time).getTime();
  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return false;

  const currentTime = now.getTime();
  return startTime <= currentTime && currentTime < endTime;
};

const canStartScheduledNotetaker = (event: GoogleCalendarEvent): boolean =>
  event.status === 'Scheduled' &&
  isActiveCalendarEvent(event) &&
  startableNotetakerStatuses.includes(calendarNotetakerStatus(event));

const isScheduledNotetakerJoining = (event: GoogleCalendarEvent): boolean =>
  event.status === 'Scheduled' &&
  isActiveCalendarEvent(event) &&
  ['starting', 'signing_in', 'joining'].includes(calendarNotetakerStatus(event));

const isRecurringCalendarEvent = (event: GoogleCalendarEvent): boolean =>
  Boolean(event.recurring_event_id);

const scheduledMeetingScopeTargets = (
  sourceEvent: GoogleCalendarEvent,
  scope: ScheduledMeetingRecurrenceScope,
  allEvents: GoogleCalendarEvent[],
): GoogleCalendarEvent[] => {
  if (scope === 'this_event' || !sourceEvent.recurring_event_id) {
    return [sourceEvent];
  }

  const sourceStart = new Date(sourceEvent.start_time).getTime();
  return allEvents
    .filter((event) => {
      const sameCalendar =
        !sourceEvent.calendar_id ||
        !event.calendar_id ||
        event.calendar_id === sourceEvent.calendar_id;
      return (
        event.oauth_id === sourceEvent.oauth_id &&
        sameCalendar &&
        event.recurring_event_id === sourceEvent.recurring_event_id &&
        new Date(event.start_time).getTime() >= sourceStart
      );
    })
    .sort(
      (left, right) => new Date(left.start_time).getTime() - new Date(right.start_time).getTime(),
    );
};

const optimisticScheduledNotetakerStatus = (
  event: GoogleCalendarEvent,
  status: GoogleCalendarEvent['status'],
): CalendarNotetakerStatus => {
  if (status === 'UserDisabled') return 'disabled';
  return isActiveCalendarEvent(event) ? 'starting' : 'scheduled';
};

const normalizedEmail = (email: string | null | undefined): string | null => {
  const trimmedEmail = email?.trim();
  return trimmedEmail ? trimmedEmail.toLowerCase() : null;
};

const normalizedPhone = (phone: string | null | undefined): string | null => {
  const digits = phone?.replace(/\D/g, '');
  return digits ? digits : null;
};

const displayName = (name: string | null | undefined): string | null => {
  const trimmedName = name?.trim();
  return trimmedName ? trimmedName : null;
};

const phoneForAttendee = (attendee: MeetingAttendee): string | null =>
  attendee.phone_number?.trim() || attendee.phone?.trim() || null;

const attendeeRecordsFromSources = ({
  attendees,
  attendeeEmails,
}: {
  attendees?: MeetingAttendee[] | null;
  attendeeEmails?: string[] | null;
}): MeetingAttendee[] => {
  const attendeeRecords = Array.isArray(attendees) ? attendees : [];
  const emailRecords = Array.isArray(attendeeEmails) ? attendeeEmails : [];
  const seen = new Set<string>();
  const records: MeetingAttendee[] = [];

  const addRecord = (attendee: MeetingAttendee) => {
    const emailKey = normalizedEmail(attendee.email);
    const phoneKey = normalizedPhone(phoneForAttendee(attendee));
    const nameKey = displayName(attendee.name)?.toLowerCase() ?? null;
    const key = emailKey
      ? `email:${emailKey}`
      : phoneKey
        ? `phone:${phoneKey}`
        : nameKey
          ? `name:${nameKey}`
          : null;

    if (!key || seen.has(key)) return;
    seen.add(key);
    records.push(attendee);
  };

  attendeeRecords.forEach(addRecord);
  emailRecords.forEach((email) => {
    if (typeof email === 'string') addRecord({ email });
  });

  return records;
};

const invitedAttendeeRecordsForEvent = (
  event: GoogleCalendarEvent | AttendedMeeting | null,
): MeetingAttendee[] => {
  if (!event) return [];

  const explicitInvitedRecords = attendeeRecordsFromSources({
    attendees: event.invited_attendees,
    attendeeEmails: event.invited_attendee_emails,
  });
  if (explicitInvitedRecords.length > 0) return explicitInvitedRecords;

  return attendeeRecordsFromSources({
    attendees: isAttendedMeeting(event) ? null : event.attendees,
    attendeeEmails: event.attendee_emails,
  });
};

const attendedAttendeeRecordsForEvent = (
  event: GoogleCalendarEvent | AttendedMeeting | null,
): MeetingAttendee[] =>
  event && isAttendedMeeting(event)
    ? attendeeRecordsFromSources({
        attendees: event.attended_attendees ?? event.attendees,
        attendeeEmails: event.attended_attendee_emails ?? event.attendee_emails,
      })
    : [];

const buildAttendeeDisplays = ({
  attendees,
  investors,
  members,
}: {
  attendees: MeetingAttendee[];
  investors: InvestorProfile[];
  members: MembersData[];
}): MeetingAttendeeDisplay[] => {
  const investorsByEmail = new Map(
    investors
      .map((investor) => [normalizedEmail(investor.email), investor] as const)
      .filter(([email]) => email !== null),
  );
  const investorsByPhone = new Map(
    investors
      .map((investor) => [normalizedPhone(investor.phone_number), investor] as const)
      .filter(([phone]) => phone !== null),
  );
  const membersByEmail = new Map(
    members
      .map((member) => [normalizedEmail(member.email), member] as const)
      .filter(([email]) => email !== null),
  );

  return attendees.map((attendee, index) => {
    const email = attendee.email?.trim() || null;
    const emailKey = normalizedEmail(email);
    const phone = phoneForAttendee(attendee);
    const phoneKey = normalizedPhone(phone);
    const investor =
      (emailKey ? investorsByEmail.get(emailKey) : undefined) ??
      (phoneKey ? investorsByPhone.get(phoneKey) : undefined);

    if (investor) {
      return {
        key: `investor:${investor.id}:${index}`,
        label: displayName(investor.name) ?? email ?? phone ?? 'Investor',
        investorId: investor.id,
      };
    }

    const member = emailKey ? membersByEmail.get(emailKey) : undefined;
    if (member) {
      return {
        key: `member:${member.id}:${index}`,
        label: displayName(member.name) ?? displayName(attendee.name) ?? member.email,
      };
    }

    const label = displayName(attendee.name) ?? email ?? phone ?? 'Attendee';

    return {
      key: `${emailKey ?? phoneKey ?? label}:${index}`,
      label,
      phoneHref: !email && phone ? `tel:${phoneKey ?? phone}` : undefined,
    };
  });
};

const MeetingHub: React.FC = () => {
  const [, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedGoogleAccount[]>([]);
  const [, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const searchParamKey = searchParams.toString();
  const meetingHubTarget = useMemo(
    () => meetingHubTargetFromSearchParams(searchParams),
    [searchParams],
  );

  const [selectedAccount, setSelectedAccount] = useState<ConnectedGoogleAccount | null>(null);
  const [events, setEvents] = useState<GoogleCalendarEvent[]>([]);
  const [attendedMeetings, setAttendedMeetings] = useState<AttendedMeeting[] | null>(null);
  const [attendeeInvestors, setAttendeeInvestors] = useState<InvestorProfile[]>([]);
  const [attendeeMembers, setAttendeeMembers] = useState<MembersData[]>([]);

  const [selectedEvent, setSelectedEvent] = useState<GoogleCalendarEvent | AttendedMeeting | null>(
    null,
  );
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [, forceSelectedEventClockTick] = useState(0);

  const [activeTabKey, setActiveTabKey] = useState<string>(() => initialLeftPanelTab(searchParams));
  const [lastListTabKey, setLastListTabKey] = useState<LeftPanelTabKey>(() => {
    const initialTabKey = initialLeftPanelTab(searchParams);
    return initialTabKey;
  });
  const [searchText, setSearchText] = useState<string>('');
  const [pastOpenPanelKeys, setPastOpenPanelKeys] = useState<string[]>(() => {
    return defaultPastOpenPanelKeys(meetingHubTarget?.artifact ?? null);
  });
  const [leftPanelLoadingByTab, setLeftPanelLoadingByTab] = useState<
    Record<LeftPanelTabKey, boolean>
  >({
    upcoming: false,
    past: false,
    settings: false,
  });
  const [updatingScheduleIds, setUpdatingScheduleIds] = useState<Set<string>>(() => new Set());
  const updatingScheduleIdsRef = useRef<Set<string>>(new Set());
  const [pendingNotetakerScopeChange, setPendingNotetakerScopeChange] =
    useState<PendingNotetakerScopeChange | null>(null);
  const [notetakerRecurrenceScope, setNotetakerRecurrenceScope] =
    useState<ScheduledMeetingRecurrenceScope>('this_event');
  const [notetakerScopeConfirmLoading, setNotetakerScopeConfirmLoading] = useState(false);
  const leftPanelRequestIds = useRef<Record<LeftPanelTabKey, number>>({
    upcoming: 0,
    past: 0,
    settings: 0,
  });
  const appliedMeetingHubTarget = useRef<string | null>(null);
  const refreshFlag = useRef(false); // 标志位
  const [saveNotesLoading, setSaveNotesLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [connectLoading, setConnectLoading] = useState<boolean>(false);
  const [startNotetakerLoading, setStartNotetakerLoading] = useState<boolean>(false);
  const [startingScheduleId, setStartingScheduleId] = useState<string | null>(null);
  const startingScheduleIdsRef = useRef<Set<string>>(new Set());

  const [notesContent, setNotesContent] = useState<string | null>(null);
  const [transcriptContent, setTranscriptContent] = useState<string | null>(null);
  const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
  const [isEditingNotes, setIsEditingNotes] = useState<boolean>(false);
  const [editedNotesContent, setEditedNotesContent] = useState<string>(notesContent || '');
  const [loadingTranscript, setLoadingTranscript] = useState<boolean>(false);
  const pastOpenPanelKeysRef = useRef<string[]>(pastOpenPanelKeys);
  const trackedArtifactViewsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    pastOpenPanelKeysRef.current = pastOpenPanelKeys;
  }, [pastOpenPanelKeys]);

  const isPastArtifactPanelOpen = useCallback(
    (artifact: 'notes' | 'transcript') => pastOpenPanelKeysRef.current.includes(artifact),
    [],
  );

  const trackPastArtifactView = useCallback(
    (
      artifact: 'notes' | 'transcript',
      meeting: AttendedMeeting,
      result: 'succeeded' | 'failed',
    ) => {
      const eventKey = `${meeting.job_id}:${artifact}:${result}`;
      if (trackedArtifactViewsRef.current.has(eventKey)) return;

      trackedArtifactViewsRef.current.add(eventKey);
      trackMeetingNotesUsage({
        action: 'view',
        artifact,
        meeting,
        result,
      });
    },
    [],
  );

  const setLeftPanelTabLoading = (tab: LeftPanelTabKey, isLoading: boolean) => {
    setLeftPanelLoadingByTab((loadingByTab) => ({
      ...loadingByTab,
      [tab]: isLoading,
    }));
  };

  const beginLeftPanelRequest = (
    tab: LeftPanelTabKey,
    { showLoading = true }: RefreshLeftPanelOptions = {},
  ) => {
    const requestId = leftPanelRequestIds.current[tab] + 1;
    leftPanelRequestIds.current[tab] = requestId;
    if (showLoading) {
      setLeftPanelTabLoading(tab, true);
    }

    return () => leftPanelRequestIds.current[tab] === requestId;
  };

  const refreshEvents = async ({ showLoading = true }: RefreshLeftPanelOptions = {}) => {
    const isLatestRequest = beginLeftPanelRequest('upcoming', { showLoading });
    try {
      try {
        await syncGoogleCalendarEvents();
      } catch (syncError) {
        console.error('Failed to refresh Google calendar sync before loading meetings:', syncError);
      }
      const data: GoogleCalendarEventResponse = await getGoogleCalendarEvents();
      if (isLatestRequest()) {
        setEvents(data.scheduled_meetings);
        setSelectedEvent((currentEvent) => {
          if (!currentEvent || !('event_id' in currentEvent)) return currentEvent;
          return (
            data.scheduled_meetings.find((event) => event.event_id === currentEvent.event_id) ??
            currentEvent
          );
        });
      }
    } catch (error) {
      if (isLatestRequest()) {
        setError('Failed to fetch events. ' + JSON.stringify(error));
      }
    } finally {
      if (isLatestRequest()) {
        setLeftPanelTabLoading('upcoming', false);
      }
    }
  };

  const refreshAttendedMeetings = async ({ showLoading = true }: RefreshLeftPanelOptions = {}) => {
    const isLatestRequest = beginLeftPanelRequest('past', { showLoading });
    try {
      let directlyMatchedMeeting: AttendedMeeting | null = null;
      if (meetingHubTarget?.meetingJobId) {
        try {
          directlyMatchedMeeting = await getAttendedMeeting(meetingHubTarget.meetingJobId);
        } catch (lookupError) {
          console.error('Failed to look up attended meeting by job id:', lookupError);
        }
      }
      const firstPage: GetAttendedMeetingsResponse = await getAttendedMeetings(
        meetingHubTarget ? { limit: meetingTargetLookupLimit } : undefined,
      );
      const meetings = directlyMatchedMeeting
        ? [
            directlyMatchedMeeting,
            ...firstPage.meetings.filter(
              (meeting) => meeting.job_id !== directlyMatchedMeeting?.job_id,
            ),
          ]
        : [...firstPage.meetings];
      let paginationKey = firstPage.pagination_key_base64 ?? null;
      let pageCount = 1;

      while (
        meetingHubTarget &&
        paginationKey &&
        pageCount < meetingTargetLookupMaxPages &&
        !meetings.some((meeting) => meetingMatchesTarget(meeting, meetingHubTarget))
      ) {
        const nextPage: GetAttendedMeetingsResponse = await getAttendedMeetings({
          paginationKey,
          limit: meetingTargetLookupLimit,
        });
        meetings.push(...nextPage.meetings);
        paginationKey = nextPage.pagination_key_base64 ?? null;
        pageCount += 1;
      }

      if (isLatestRequest()) {
        setAttendedMeetings(meetings);
      }
    } catch (error) {
      if (isLatestRequest()) {
        setError('Failed to fetch attended meetings. ' + JSON.stringify(error));
      }
    } finally {
      if (isLatestRequest()) {
        setLeftPanelTabLoading('past', false);
      }
    }
  };

  const refreshAccounts = async ({ showLoading = true }: RefreshLeftPanelOptions = {}) => {
    const isLatestRequest = beginLeftPanelRequest('settings', { showLoading });
    try {
      const data: GetGoogleAccountResponse = await getConnectedGoogleAccounts();

      if (isLatestRequest()) {
        if (data.google_accounts.length === 0) {
          const fakeData: ConnectedGoogleAccount[] = [
            {
              email: '',
              calendars: [],
            },
          ];
          setConnectedAccounts(fakeData);
        } else {
          setConnectedAccounts(data.google_accounts);
        }
      }
    } catch (error) {
      if (isLatestRequest()) {
        setError('Failed to fetch connected accounts. ' + JSON.stringify(error));
      }
    } finally {
      if (isLatestRequest()) {
        setLeftPanelTabLoading('settings', false);
      }
    }
  };

  useEffect(() => {
    const loadDataForTab = async (key: string) => {
      if (refreshFlag.current) {
        refreshFlag.current = false;
        return;
      }
      refreshFlag.current = true;
      try {
        switch (key) {
          case 'upcoming':
            refreshEvents();
            break;
          case 'past':
            refreshAttendedMeetings();
            break;
          case 'settings':
            refreshAccounts();
            break;
          default:
            break;
        }
      } finally {
        refreshFlag.current = false;
      }
    };

    loadDataForTab(activeTabKey);
  }, [activeTabKey]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchAllInvestors = async () => {
      const limit = 1024;
      const maxPages = 20;
      let offset = 0;
      let page = await getInvestors({ limit, offset });
      const investors = [...page];
      let pageCount = 1;

      while (page.length === limit && pageCount < maxPages) {
        offset += limit;
        page = await getInvestors({ limit, offset });
        investors.push(...page);
        pageCount += 1;
      }

      return investors;
    };

    const loadAttendeeLookups = async () => {
      const [investorResult, memberResult] = await Promise.allSettled([
        fetchAllInvestors(),
        getMembers(),
      ]);

      if (!isMounted) return;

      if (investorResult.status === 'fulfilled') {
        setAttendeeInvestors(investorResult.value);
      }
      if (memberResult.status === 'fulfilled') {
        setAttendeeMembers(memberResult.value.members);
      }
    };

    loadAttendeeLookups();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleConnectAccount = async (account?: ConnectedGoogleAccount) => {
    setConnectLoading(true);
    if (account) {
      try {
        handleAccountClick(account);
        await disconnectGoogleAccount(account.email);
        setSelectedAccount(null);
        await refreshAccounts();
      } catch (error) {
        message.error('Failed to disconnect Google Account. ' + JSON.stringify(error));
      } finally {
        setConnectLoading(false);
      }
    } else {
      try {
        const { authUrl }: ConnectNewGoogleAccountResponse = await connectNewGoogleAccount();
        const cleanedAuthUrl = removeSpacesAndStrs(authUrl, ['openid', 'profile', 'email']);
        window.location.href = cleanedAuthUrl;
      } catch (error) {
        message.error('Failed to connect to Google Account. ' + JSON.stringify(error));
      } finally {
        setConnectLoading(false);
      }
    }
  };

  const handleTabChange = (key: string) => {
    if (isLeftPanelTabKey(key)) {
      setLastListTabKey(key);
    }
    setActiveTabKey(key);
    setSelectedEvent(null);
    setSelectedEventId(null);
    setSelectedAccount(null);
    if (key !== 'past') {
      setPastOpenPanelKeys([]);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const handleEventClick = (event: GoogleCalendarEvent) => {
    if (activeTabKey === 'add-meeting') {
      setActiveTabKey('upcoming');
    }
    setSelectedEvent(event);
    setSelectedEventId(event.event_id);
    setNotesContent(null);
    setTranscriptContent(null);
  };

  const setSchedulesUpdating = (scheduleIds: string[], isUpdating: boolean) => {
    const next = new Set(updatingScheduleIdsRef.current);
    scheduleIds.forEach((scheduleId) => {
      if (isUpdating) {
        next.add(scheduleId);
      } else {
        next.delete(scheduleId);
      }
    });

    updatingScheduleIdsRef.current = next;
    setUpdatingScheduleIds(next);
  };

  const applyScheduledMeetingPatch = (scheduleId: string, patch: Partial<GoogleCalendarEvent>) => {
    setEvents((currentEvents) =>
      currentEvents.map((event) =>
        event.schedule_id === scheduleId ? { ...event, ...patch } : event,
      ),
    );
    setSelectedEvent((currentEvent) => {
      if (!currentEvent || isAttendedMeeting(currentEvent)) return currentEvent;
      return currentEvent.schedule_id === scheduleId ? { ...currentEvent, ...patch } : currentEvent;
    });
  };

  const applyScheduledMeetingPatches = (
    patchesByScheduleId: Map<string, Partial<GoogleCalendarEvent>>,
  ) => {
    setEvents((currentEvents) =>
      currentEvents.map((event) => {
        const patch = patchesByScheduleId.get(event.schedule_id);
        return patch ? { ...event, ...patch } : event;
      }),
    );
    setSelectedEvent((currentEvent) => {
      if (!currentEvent || isAttendedMeeting(currentEvent)) return currentEvent;
      const patch = patchesByScheduleId.get(currentEvent.schedule_id);
      return patch ? { ...currentEvent, ...patch } : currentEvent;
    });
  };

  const commitScheduledMeetingStatusChange = async (
    event: GoogleCalendarEvent,
    checked: boolean,
    scope: ScheduledMeetingRecurrenceScope = 'this_event',
  ) => {
    if (updatingScheduleIdsRef.current.has(event.schedule_id)) {
      return;
    }

    const nextStatus: GoogleCalendarEvent['status'] = checked ? 'Scheduled' : 'UserDisabled';
    const currentEvent = events.find((candidate) => candidate.schedule_id === event.schedule_id);
    const sourceEvent = currentEvent ?? event;
    const targetEvents = scheduledMeetingScopeTargets(sourceEvent, scope, events);
    if (targetEvents.length === 0) {
      targetEvents.push(sourceEvent);
    }
    const targetScheduleIds = targetEvents.map((targetEvent) => targetEvent.schedule_id);
    if (targetScheduleIds.some((scheduleId) => updatingScheduleIdsRef.current.has(scheduleId))) {
      message.info('Another notetaker update for this series is still in progress.');
      return;
    }

    const optimisticPatches = new Map<string, Partial<GoogleCalendarEvent>>();
    const rollbackPatches = new Map<string, Partial<GoogleCalendarEvent>>();

    targetEvents.forEach((targetEvent) => {
      optimisticPatches.set(targetEvent.schedule_id, {
        status: nextStatus,
        notetaker_status: optimisticScheduledNotetakerStatus(targetEvent, nextStatus),
      });
      rollbackPatches.set(targetEvent.schedule_id, {
        status: targetEvent.status,
        notetaker_status: targetEvent.notetaker_status,
      });
    });

    applyScheduledMeetingPatches(optimisticPatches);
    setSchedulesUpdating(targetScheduleIds, true);

    try {
      await updateFutureMeetings({
        patches: [
          {
            op: 'replace',
            schedule_id: event.schedule_id,
            field: 'status',
            value: nextStatus,
            scope,
          },
        ],
      });
      await refreshEvents({ showLoading: false });
    } catch (error) {
      applyScheduledMeetingPatches(rollbackPatches);
      message.error('Failed to update event status. ' + JSON.stringify(error));
    } finally {
      setSchedulesUpdating(targetScheduleIds, false);
    }
  };

  const handleScheduledMeetingStatusChange = (event: GoogleCalendarEvent, checked: boolean) => {
    if (updatingScheduleIdsRef.current.has(event.schedule_id)) {
      return;
    }

    if (isRecurringCalendarEvent(event)) {
      setPendingNotetakerScopeChange({ event, checked });
      setNotetakerRecurrenceScope('this_event');
      return;
    }

    void commitScheduledMeetingStatusChange(event, checked);
  };

  const handleNotetakerScopeCancel = () => {
    setPendingNotetakerScopeChange(null);
    setNotetakerScopeConfirmLoading(false);
  };

  const handleNotetakerScopeConfirm = async () => {
    if (!pendingNotetakerScopeChange) return;

    setNotetakerScopeConfirmLoading(true);
    try {
      await commitScheduledMeetingStatusChange(
        pendingNotetakerScopeChange.event,
        pendingNotetakerScopeChange.checked,
        notetakerRecurrenceScope,
      );
      setPendingNotetakerScopeChange(null);
    } finally {
      setNotetakerScopeConfirmLoading(false);
    }
  };

  const handleMeetingClick = (meeting: AttendedMeeting) => {
    if (activeTabKey === 'add-meeting') {
      setActiveTabKey('past');
    }
    const attendedMeeting = selectedEvent as AttendedMeeting;
    if (attendedMeeting && attendedMeeting.summary_file_key) {
      cancelDocumentRequest(attendedMeeting.summary_file_key);
    }
    if (attendedMeeting && attendedMeeting.transcript_file_key) {
      cancelDocumentRequest(attendedMeeting.transcript_file_key);
    }
    setNotesContent(null);
    setTranscriptContent(null);
    setSelectedEvent(meeting);
    setSelectedEventId(meeting.job_id);
    setPastOpenPanelKeys(defaultPastOpenPanelKeys(null));
    trackMeetingNotesUsage({
      action: 'view',
      artifact: 'meeting',
      meeting,
      result: 'succeeded',
    });
  };

  useEffect(() => {
    if (selectedEvent) {
      if ('summary_file_key' in selectedEvent) {
        const attendedMeeting = selectedEvent as AttendedMeeting;
        const fetchNotes = async () => {
          try {
            setLoadingNotes(true);
            if (attendedMeeting.summary_file_key) {
              const res: GetDocumentResponse = await getDocument(attendedMeeting.summary_file_key);
              setNotesContent(res.document.content);
            }
          } catch (error) {
            if (isPastArtifactPanelOpen('notes')) {
              trackPastArtifactView('notes', attendedMeeting, 'failed');
            }
            setError('Failed to fetch notes. ' + JSON.stringify(error));
          }
          setLoadingNotes(false);
        };
        fetchNotes();
      }

      if ('transcript_file_key' in selectedEvent) {
        const attendedMeeting = selectedEvent as AttendedMeeting;
        const fetchTranscript = async () => {
          try {
            setLoadingTranscript(true);
            if (attendedMeeting.transcript_file_key) {
              const res: GetDocumentResponse = await getDocument(
                attendedMeeting.transcript_file_key,
              );
              setTranscriptContent(res.document.content);
            }
          } catch (error) {
            if (isPastArtifactPanelOpen('transcript')) {
              trackPastArtifactView('transcript', attendedMeeting, 'failed');
            }
            setError('Failed to fetch transcript. ' + JSON.stringify(error));
          }
          setLoadingTranscript(false);
        };
        fetchTranscript();
      }
    }
  }, [isPastArtifactPanelOpen, selectedEvent, trackPastArtifactView]);

  useEffect(() => {
    if (!selectedEvent || !isAttendedMeeting(selectedEvent)) return;

    if (notesContent && pastOpenPanelKeys.includes('notes')) {
      trackPastArtifactView('notes', selectedEvent, 'succeeded');
    }
    if (transcriptContent && pastOpenPanelKeys.includes('transcript')) {
      trackPastArtifactView('transcript', selectedEvent, 'succeeded');
    }
  }, [notesContent, pastOpenPanelKeys, selectedEvent, trackPastArtifactView, transcriptContent]);

  useEffect(() => {
    if (!selectedEvent || !('schedule_id' in selectedEvent)) {
      return undefined;
    }

    const startTime = new Date(selectedEvent.start_time).getTime();
    const endTime = new Date(selectedEvent.end_time).getTime();
    const now = Date.now();
    if (Number.isNaN(startTime) || Number.isNaN(endTime) || now >= endTime) {
      return undefined;
    }

    const rerenderForClockChange = () => forceSelectedEventClockTick((tick) => tick + 1);
    const timerIds: number[] = [];
    const scheduleBoundaryTick = (boundaryTime: number) => {
      const delay = boundaryTime - Date.now();
      if (delay > 0) {
        timerIds.push(window.setTimeout(rerenderForClockChange, Math.min(delay, 2147483647)));
      }
    };

    scheduleBoundaryTick(startTime);
    scheduleBoundaryTick(endTime);
    const intervalId = window.setInterval(rerenderForClockChange, 30_000);

    return () => {
      window.clearInterval(intervalId);
      timerIds.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [selectedEvent]);

  useEffect(() => {
    if (!meetingHubTarget || attendedMeetings === null) return;
    if (appliedMeetingHubTarget.current === searchParamKey) return;

    const targetMeeting = attendedMeetings.find((meeting) =>
      meetingMatchesTarget(meeting, meetingHubTarget),
    );
    if (!targetMeeting) {
      appliedMeetingHubTarget.current = searchParamKey;
      message.info('That meeting is no longer available in Meeting Hub.');
      return;
    }

    appliedMeetingHubTarget.current = searchParamKey;
    setActiveTabKey('past');
    setSelectedEvent(targetMeeting);
    setSelectedEventId(targetMeeting.job_id);
    trackMeetingNotesUsage({
      action: 'view',
      artifact: 'meeting',
      meeting: targetMeeting,
      result: 'succeeded',
    });

    setPastOpenPanelKeys(defaultPastOpenPanelKeys(meetingHubTarget.artifact));
  }, [attendedMeetings, meetingHubTarget, searchParamKey]);

  const handleAccountClick = (account: ConnectedGoogleAccount) => {
    if (activeTabKey === 'add-meeting') {
      setActiveTabKey('settings');
    }
    setSelectedAccount(account);
  };

  const groupedEvents = events.reduce((acc: Record<string, GoogleCalendarEvent[]>, event) => {
    const date = new Date(event.start_time).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filteredUpcomingEvents = Object.keys(groupedEvents)
    .map((date) => ({
      date,
      events: groupedEvents[date].filter((event) =>
        event.title.toLowerCase().includes(normalizedSearchText),
      ),
    }))
    .filter((group) => group.events.length > 0);

  const groupedPastMeetings =
    attendedMeetings?.reduce((acc: Record<string, AttendedMeeting[]>, meeting) => {
      const date = new Date(meeting.start_time).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(meeting);
      return acc;
    }, {}) || {};

  const filteredPastMeetings = Object.keys(groupedPastMeetings)
    .map((date) => ({
      date,
      events: groupedPastMeetings[date].filter((meeting) =>
        meeting.title.toLowerCase().includes(normalizedSearchText),
      ),
    }))
    .filter((group) => group.events.length > 0);

  const hasSearchText = normalizedSearchText.length > 0;
  const upcomingEmptyDescription = hasSearchText
    ? 'No upcoming events match your search'
    : 'No upcoming events';
  const pastEmptyDescription = hasSearchText
    ? 'No past events match your search'
    : 'No past events';

  const renderEventEmptyState = (description: string) => (
    <div className="event-empty-state">
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
    </div>
  );

  const renderNotetakerStatusTag = (
    event: GoogleCalendarEvent,
    className = '',
  ): React.ReactNode => {
    const status = calendarNotetakerStatus(event);
    const statusCopy = notetakerStatusCopy[status];

    return (
      <Tag
        className={`notetaker-status-tag notetaker-status-tag-${status} ${className}`.trim()}
        color={statusCopy.color}
      >
        {statusCopy.label}
      </Tag>
    );
  };

  const renderNotetakerStatusHint = (event: GoogleCalendarEvent): React.ReactNode => {
    const hint = notetakerStatusCopy[calendarNotetakerStatus(event)].hint;
    if (!hint) return null;

    return <div className="notetaker-status-hint">{hint}</div>;
  };

  const renderNotetakerReadiness = (event: GoogleCalendarEvent): React.ReactNode => {
    const invitationState = event.notetaker_invitation_state;
    if (!invitationState || invitationState === 'not_required') return null;

    const { copy, classNameState } = getNotetakerInvitationCopy(event);
    const botEmail = event.notetaker_bot_account_email?.trim();

    return (
      <div className={`notetaker-readiness notetaker-readiness-${classNameState}`}>
        <div className="notetaker-readiness-title">{copy.label}</div>
        {botEmail && <div>Assigned bot: {botEmail}</div>}
        {copy.hint && <div>{copy.hint}</div>}
      </div>
    );
  };

  const handleStartScheduledNotetaker = async (event: GoogleCalendarEvent) => {
    if (startingScheduleIdsRef.current.has(event.schedule_id)) {
      return;
    }

    startingScheduleIdsRef.current.add(event.schedule_id);
    setStartingScheduleId(event.schedule_id);
    applyScheduledMeetingPatch(event.schedule_id, {
      notetaker_status: 'starting',
    });
    try {
      const data: StartScheduledMeetingNowResponse = await startScheduledMeetingNow(
        event.schedule_id,
      );
      applyScheduledMeetingPatch(event.schedule_id, data.scheduled_meeting);
      message.success('Notetaker start requested');
      await refreshEvents({ showLoading: false });
      await refreshAttendedMeetings({ showLoading: false });
    } catch (error) {
      applyScheduledMeetingPatch(event.schedule_id, {
        notetaker_status: event.notetaker_status,
      });
      message.error('Failed to start notetaker. ' + JSON.stringify(error));
    } finally {
      startingScheduleIdsRef.current.delete(event.schedule_id);
      setStartingScheduleId(null);
    }
  };

  const shouldShowUpcomingLoading = leftPanelLoadingByTab.upcoming && events.length === 0;
  const shouldShowPastLoading = leftPanelLoadingByTab.past && attendedMeetings === null;
  const shouldShowSettingsLoading =
    leftPanelLoadingByTab.settings && connectedAccounts.length === 0;

  const tabItems = [
    {
      key: 'upcoming',
      label: 'Upcoming Events',
      children: shouldShowUpcomingLoading ? (
        <div className="loadingContainer">
          <Spin />
        </div>
      ) : (
        <div className="tabs-content">
          {filteredUpcomingEvents.length === 0 && renderEventEmptyState(upcomingEmptyDescription)}
          {filteredUpcomingEvents.map((group) => (
            <div className="events upcoming" key={group.date}>
              <div className="event-group-title">{group.date}</div>
              <List
                dataSource={group.events}
                renderItem={(event) => (
                  <List.Item
                    key={event.event_id}
                    className={selectedEventId === event.event_id ? 'selected' : ''}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="event-item">
                      <div className="event-time-title">
                        <div className="event-time">
                          {new Date(event.start_time).toLocaleTimeString()}
                        </div>
                        <div className="event-title">{event.title}</div>
                      </div>
                      <div
                        className="event-actions"
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                      >
                        {renderNotetakerStatusTag(event, 'event-row-status')}
                        <span
                          className="notetaker-toggle-control"
                          onClick={(clickEvent) => clickEvent.stopPropagation()}
                          onPointerDown={(pointerEvent) => pointerEvent.stopPropagation()}
                        >
                          <Switch
                            aria-label={`Toggle notetaker for ${event.title}`}
                            checked={event.status === 'Scheduled'}
                            className="meeting-notetaker-switch"
                            disabled={updatingScheduleIds.has(event.schedule_id)}
                            onChange={(checked) =>
                              handleScheduledMeetingStatusChange(event, checked)
                            }
                            size="small"
                          />
                        </span>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'past',
      label: 'Past Events',
      children: shouldShowPastLoading ? (
        <div className="loadingContainer">
          <Spin />
        </div>
      ) : (
        <div className="tabs-content">
          {filteredPastMeetings.length === 0 && renderEventEmptyState(pastEmptyDescription)}
          {filteredPastMeetings.map((group) => (
            <div className="events past" key={group.date}>
              <div className="event-group-title">{group.date}</div>
              <List
                dataSource={group.events}
                renderItem={(meeting) => (
                  <List.Item
                    key={meeting.job_id}
                    onClick={() => handleMeetingClick(meeting)}
                    className={selectedEventId === meeting.job_id ? 'selected' : ''}
                  >
                    <div className="event-item">
                      <div className="event-time-title">
                        <div className="event-time">
                          {new Date(meeting.start_time).toLocaleTimeString()}
                        </div>
                        <div className="event-title" title={meeting.title}>
                          {meeting.title}
                        </div>
                      </div>
                      <NotesIcon />
                    </div>
                  </List.Item>
                )}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'settings',
      label: <SettingOutlined />,
      children: shouldShowSettingsLoading ? (
        <div className="loadingContainer">
          <Spin />
        </div>
      ) : (
        <div className="tabs-content">
          <div className="events settings">
            <div className="add-account-text">Connect your calendar</div>
            <List
              dataSource={connectedAccounts}
              renderItem={(account) => (
                <List.Item
                  key={account.email || 'empty'}
                  className={selectedAccount?.email === account.email ? 'selected' : ''}
                  onClick={() => account.email && handleAccountClick(account)}
                >
                  {account.email ? (
                    <div className="event-item">
                      <div className="account-email">
                        <img
                          src={GoogleCalendarIcon}
                          alt="Google Calendar Icon"
                          className="google-calendar-icon"
                        />
                        <div className="account-name">
                          {getGoogleCalendarAccountDisplayName(account)}
                        </div>
                      </div>
                      <Button
                        className="connect-button"
                        type={selectedAccount?.email === account.email ? 'default' : 'primary'}
                        loading={connectLoading}
                        onClick={() => handleConnectAccount(account)}
                      >
                        {'Disconnect'}
                      </Button>
                    </div>
                  ) : (
                    <div className="event-item">
                      <div className="account-email">
                        <img
                          src={GoogleCalendarIcon}
                          alt="Google Calendar Icon"
                          className="google-calendar-icon"
                        />
                        <div className="account-name">
                          {getGoogleCalendarAccountDisplayName(account)}
                        </div>
                      </div>
                      <Button
                        type="primary"
                        className="connect-button"
                        loading={connectLoading}
                        onClick={() => handleConnectAccount()}
                      >
                        Connect
                      </Button>
                    </div>
                  )}
                </List.Item>
              )}
            />
          </div>
        </div>
      ),
    },
  ];

  const handleEditNotes = () => {
    trackMeetingNotesUsage({
      action: 'edit',
      artifact: 'notes',
      meeting: selectedEvent,
    });
    setIsEditingNotes(true);
    setEditedNotesContent(notesContent || '');
  };

  const handleSaveNotes = async () => {
    const meeting = selectedEvent;
    if (!meeting || !isAttendedMeeting(meeting) || !meeting.summary_file_key) return;

    setSaveNotesLoading(true);
    try {
      await updateDocumentContent(meeting.summary_file_key, editedNotesContent);
      message.success('Notes saved successfully');
      setIsEditingNotes(false);
      setNotesContent(editedNotesContent);
      trackMeetingNotesUsage({
        action: 'save',
        artifact: 'notes',
        meeting,
        result: 'succeeded',
      });
    } catch (error) {
      trackMeetingNotesUsage({
        action: 'save',
        artifact: 'notes',
        meeting,
        result: 'failed',
      });
      message.error('Failed to save notes. ' + JSON.stringify(error));
    } finally {
      setSaveNotesLoading(false);
    }
  };

  const handleCancelEditNotes = () => {
    setIsEditingNotes(false);
    setEditedNotesContent(notesContent || '');
  };

  const copyNotes = () => {
    const meeting = selectedEvent;
    if (meeting && notesContent) {
      navigator.clipboard
        .writeText(notesContent)
        .then(() => {
          message.success('Notes copied to clipboard');
          trackMeetingNotesUsage({
            action: 'copy',
            artifact: 'notes',
            meeting,
            result: 'succeeded',
          });
        })
        .catch(() => {
          trackMeetingNotesUsage({
            action: 'copy',
            artifact: 'notes',
            meeting,
            result: 'failed',
          });
          message.error('Failed to copy notes to clipboard');
        });
    }
  };

  const copyTranscript = () => {
    const meeting = selectedEvent;
    if (meeting && transcriptContent) {
      navigator.clipboard
        .writeText(transcriptContent)
        .then(() => {
          message.success('Transcript copied to clipboard');
          trackMeetingNotesUsage({
            action: 'copy',
            artifact: 'transcript',
            meeting,
            result: 'succeeded',
          });
        })
        .catch(() => {
          trackMeetingNotesUsage({
            action: 'copy',
            artifact: 'transcript',
            meeting,
            result: 'failed',
          });
          message.error('Failed to copy transcript to clipboard');
        });
    }
  };

  const handleArtifactControlActivation = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    action: () => void,
  ) => {
    if ('key' in event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    event.stopPropagation();
    action();
  };

  const downloadRecord = async () => {
    const meeting = selectedEvent;
    if (!meeting || !isAttendedMeeting(meeting)) return;

    setDownloadLoading(true);
    try {
      trackMeetingNotesUsage({
        action: 'download',
        artifact: 'recording',
        meeting,
      });
      const data: GenerateRecordingLinkResponse = await generateRecordingLink(meeting.job_id);
      window.location.href = data.recording;
    } catch {
      trackMeetingNotesUsage({
        action: 'download',
        artifact: 'recording',
        meeting,
        result: 'failed',
      });
      message.error('Failed to download recording.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const formatEventDate = (start_time: string, end_time?: string | null) => {
    const startDate = new Date(start_time);
    if (Number.isNaN(startDate.getTime())) return 'Date not available';

    const formattedDate = startDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const formattedStartTime = startDate
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
    const endDate = end_time ? new Date(end_time) : null;
    const formattedEndTime =
      endDate && !Number.isNaN(endDate.getTime())
        ? endDate
            .toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            })
            .toLowerCase()
        : null;

    if (!formattedEndTime) return `${formattedDate} ⋅ ${formattedStartTime}`;

    return `${formattedDate} ⋅ ${formattedStartTime} – ${formattedEndTime}`;
  };

  const renderAttendees = (attendeeRecords: MeetingAttendee[], emptyText: string) => {
    const attendees = buildAttendeeDisplays({
      attendees: attendeeRecords,
      investors: attendeeInvestors,
      members: attendeeMembers,
    });

    if (attendees.length === 0) return emptyText;

    return (
      <span className="meeting-attendee-list">
        {attendees.map((attendee, index) => (
          <React.Fragment key={attendee.key}>
            {attendee.investorId ? (
              <Link
                className="meeting-attendee-link"
                to={`/home/clients/${encodeURIComponent(attendee.investorId)}/profile`}
              >
                {attendee.label}
              </Link>
            ) : attendee.phoneHref ? (
              <a className="meeting-attendee-link" href={attendee.phoneHref}>
                {attendee.label}
              </a>
            ) : (
              <span>{attendee.label}</span>
            )}
            {index < attendees.length - 1 && <span className="meeting-attendee-separator">, </span>}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderParticipantInfoRow = (
    label: string,
    attendeeRecords: MeetingAttendee[],
    emptyText: string,
  ) => (
    <div className="event meeting-participant-row">
      <TeamOutlined className="event-info-icon" />
      <span className="event-info-content">
        <span className="event-info-label">{label}</span>
        <span className="event-info-text">{renderAttendees(attendeeRecords, emptyText)}</span>
      </span>
    </div>
  );

  const meetingHref = (meetingUrl: string): string | null => {
    const trimmedMeetingUrl = meetingUrl.trim();
    if (!trimmedMeetingUrl) return null;

    try {
      const url = new URL(trimmedMeetingUrl);
      return url.protocol === 'http:' || url.protocol === 'https:' ? trimmedMeetingUrl : null;
    } catch {
      return null;
    }
  };

  const renderMeetingLink = (meetingUrl: string) => {
    const href = meetingHref(meetingUrl);
    if (!href) return meetingUrl;

    return (
      <a className="meeting-link" href={href} rel="noopener noreferrer" target="_blank">
        {meetingUrl}
      </a>
    );
  };

  const renderMeetingMemoryStatus = (
    event: GoogleCalendarEvent | AttendedMeeting,
  ): React.ReactNode => {
    if (!isAttendedMeeting(event)) return null;

    const status = event.meeting_memory_status ?? 'not_applicable';
    const copy = meetingMemoryStatusCopy[status];
    if (!copy) return null;

    return (
      <div className={`event meeting-memory-status meeting-memory-status-${status}`}>
        <NotesIcon className="event-info-icon" />
        <span className="event-info-content">
          <span className="event-info-label">Profile Memory</span>
          <span className="event-info-text">
            <Tag color={copy.color}>{copy.label}</Tag>
            <span>{copy.hint}</span>
          </span>
        </span>
      </div>
    );
  };

  const renderMeetingInfo = (event: GoogleCalendarEvent | AttendedMeeting, meetingUrl: string) => (
    <div className="info">
      {renderParticipantInfoRow(
        'Invited',
        invitedAttendeeRecordsForEvent(event),
        'Invitees not available',
      )}
      {isAttendedMeeting(event) &&
        renderParticipantInfoRow(
          'Attended',
          attendedAttendeeRecordsForEvent(event),
          'Attendance not available',
        )}
      {renderMeetingMemoryStatus(event)}
      <div className="event linke">
        <EnvironmentOutlined className="event-info-icon" />
        <span className="event-info-text">{renderMeetingLink(meetingUrl)}</span>
      </div>
    </div>
  );

  const { token } = theme.useToken();

  const genExtra = (type: 'notes' | 'transcript') => {
    return selectedEvent ? (
      type === 'notes' ? (
        <div className="collapse-item-handle-button">
          {notesContent ? (
            <Button
              aria-label="Edit notes"
              className="handle-icon"
              htmlType="button"
              icon={<EditOutlined />}
              onClick={(event) => handleArtifactControlActivation(event, handleEditNotes)}
              onKeyDown={(event) => handleArtifactControlActivation(event, handleEditNotes)}
              type="text"
            />
          ) : null}
          <Button
            aria-label="Copy notes"
            className="handle-icon"
            htmlType="button"
            icon={<CopyOutlined />}
            onClick={(event) => handleArtifactControlActivation(event, copyNotes)}
            onKeyDown={(event) => handleArtifactControlActivation(event, copyNotes)}
            type="text"
          />
        </div>
      ) : (
        <div className="collapse-item-handle-button">
          <Button
            aria-label="Copy transcript"
            className="handle-icon"
            htmlType="button"
            icon={<CopyOutlined />}
            onClick={(event) => handleArtifactControlActivation(event, copyTranscript)}
            onKeyDown={(event) => handleArtifactControlActivation(event, copyTranscript)}
            type="text"
          />
        </div>
      )
    ) : null;
  };

  const upcomingCollapseItems = [
    {
      key: 'notes',
      label: 'Notes',
      children: loadingNotes ? <Spin tip="Loading..." /> : <p>No notes available</p>,
      style: {
        // marginBottom: 24,
        background: token.colorFillAlter,
        borderRadius: 12,
        textAlign: 'left',
        // border: "none",
      },
    },
    {
      key: 'transcript',
      label: 'Transcript',
      children: loadingTranscript ? <Spin tip="Loading..." /> : <p>No notes available</p>,
      style: {
        // marginBottom: 24,
        background: token.colorFillAlter,
        borderRadius: 12,
        textAlign: 'left',
        // border: "none",
      },
    },
  ];
  const pastCollapseItems = [
    {
      key: 'notes',
      label: 'Notes',
      children: loadingNotes ? (
        <Spin tip="Loading..." />
      ) : (
        <div>
          {notesContent ? (
            isEditingNotes ? (
              <div>
                <Input.TextArea
                  aria-label="Edit meeting notes"
                  className="meeting-notes-textarea"
                  value={editedNotesContent}
                  autoSize={{ minRows: 10, maxRows: 24 }}
                  onChange={(event) => setEditedNotesContent(event.target.value)}
                />
                <div className="meeting-notes-edit-actions">
                  <Button
                    type="primary"
                    className="meeting-notes-action-button meeting-notes-save-button"
                    loading={saveNotesLoading}
                    onClick={handleSaveNotes}
                  >
                    Save
                  </Button>
                  <Button
                    className="meeting-notes-action-button meeting-notes-cancel-button"
                    onClick={handleCancelEditNotes}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <ReactMarkdown className="left-align">{notesContent}</ReactMarkdown>
              </div>
            )
          ) : (
            <p>No notes available</p>
          )}
        </div>
      ),
      style: {
        // marginBottom: 24,
        background: token.colorFillAlter,
        borderRadius: 12,
        textAlign: 'left',
        // border: "none",
      },
      extra: genExtra('notes'),
    },
    {
      key: 'transcript',
      label: 'Transcript',
      children: loadingTranscript ? (
        <Spin tip="Loading..." />
      ) : (
        <div>
          {transcriptContent ? (
            <ReactMarkdown className="left-align">{transcriptContent}</ReactMarkdown>
          ) : (
            <p>No transcript available</p>
          )}
        </div>
      ),
      style: {
        background: token.colorFillAlter,
        borderRadius: 12,
        textAlign: 'left',
      },
      extra: genExtra('transcript'),
    },
  ];

  const handleAddMeetingClick = () => {
    if (isLeftPanelTabKey(activeTabKey)) {
      setLastListTabKey(activeTabKey);
    }
    setSelectedEvent(null);
    setSelectedEventId(null);
    setSelectedAccount(null);
    setActiveTabKey('add-meeting');
  };

  const handleFormSubmit = async (values: { meetingName: string; meetingUrl: string }) => {
    const data = {
      title: values.meetingName,
      url: values.meetingUrl,
    };
    try {
      setStartNotetakerLoading(true);
      await startImmediateMeeting(data);
      refreshAttendedMeetings();
    } catch (error) {
      setError('Failed to add meeting. ' + JSON.stringify(error));
    } finally {
      setStartNotetakerLoading(false);
    }
  };

  const detailPanelOpen =
    activeTabKey === 'add-meeting' || Boolean(selectedEvent) || Boolean(selectedAccount);
  const leftPanelActiveTabKey = isLeftPanelTabKey(activeTabKey) ? activeTabKey : lastListTabKey;

  const handleDetailBack = () => {
    if (activeTabKey === 'add-meeting') {
      setActiveTabKey(lastListTabKey);
    }
    setSelectedEvent(null);
    setSelectedEventId(null);
    setSelectedAccount(null);
    setPastOpenPanelKeys([]);
  };

  return (
    <div className={`meeting-hub${detailPanelOpen ? ' meeting-hub-detail-open' : ''}`}>
      <div className="left-panel">
        <div className="search">
          <div className="meeting-hub-search-row">
            <label className="meeting-hub-search">
              <SearchOutlined aria-hidden="true" />
              <Input
                aria-label="Search meetings"
                placeholder="Search by name or email"
                variant="borderless"
                value={searchText}
                className="search-input"
                onChange={(e) => handleSearch(e.target.value)}
              />
            </label>
            <Button
              aria-label="Add meeting"
              className="meeting-hub-add-button"
              icon={<PlusOutlined />}
              onClick={handleAddMeetingClick}
              type="primary"
            >
              Add
            </Button>
          </div>
        </div>
        <div className="tabs-container">
          <Tabs activeKey={leftPanelActiveTabKey} onChange={handleTabChange} items={tabItems} />
        </div>
      </div>
      <Modal
        className="notetaker-recurrence-modal"
        title="Apply notetaker change"
        open={Boolean(pendingNotetakerScopeChange)}
        okText="OK"
        cancelText="Cancel"
        confirmLoading={notetakerScopeConfirmLoading}
        onCancel={handleNotetakerScopeCancel}
        onOk={handleNotetakerScopeConfirm}
      >
        <Radio.Group
          className="notetaker-recurrence-options"
          value={notetakerRecurrenceScope}
          onChange={(event) => setNotetakerRecurrenceScope(event.target.value)}
        >
          <Radio value="this_event">This event</Radio>
          <Radio value="this_and_following_events">This and following events</Radio>
        </Radio.Group>
      </Modal>
      <div className="right-panel">
        {detailPanelOpen && (
          <Button
            className="meeting-hub-back-button"
            type="text"
            aria-label="Back to meetings"
            icon={<ArrowLeftOutlined />}
            onClick={handleDetailBack}
          >
            Back to meetings
          </Button>
        )}
        {activeTabKey === 'upcoming' && selectedEvent && 'event_id' in selectedEvent && (
          <div className="event-detail">
            <div className="title">
              <div>
                <div className="event-title">{selectedEvent?.title}</div>
                {selectedEvent &&
                  'start_time' in selectedEvent &&
                  formatEventDate(
                    selectedEvent.start_time,
                    'end_time' in selectedEvent ? selectedEvent.end_time : null,
                  )}
              </div>
              <div className="meeting-detail-actions">
                {renderNotetakerStatusTag(selectedEvent, 'event-detail-status')}
                {canStartScheduledNotetaker(selectedEvent) && (
                  <Button
                    type="primary"
                    className="notetaker-start-button"
                    aria-label="Start Notetaker"
                    icon={<PlayCircleOutlined />}
                    loading={startingScheduleId === selectedEvent.schedule_id}
                    onClick={() => handleStartScheduledNotetaker(selectedEvent)}
                  >
                    Start Notetaker
                  </Button>
                )}
                {isScheduledNotetakerJoining(selectedEvent) && (
                  <Button
                    type="primary"
                    className="notetaker-start-button"
                    aria-label="Notetaker joining"
                    icon={<PlayCircleOutlined />}
                    loading
                    disabled
                  >
                    Notetaker joining
                  </Button>
                )}
              </div>
            </div>
            {selectedEvent && 'meeting_link' in selectedEvent
              ? renderMeetingInfo(selectedEvent, selectedEvent.meeting_link)
              : null}
            {renderNotetakerStatusHint(selectedEvent)}
            {renderNotetakerReadiness(selectedEvent)}
            <div className="content">
              <Collapse
                items={upcomingCollapseItems}
                defaultActiveKey={artifactPanelKeys}
                expandIcon={renderCollapseIcon}
                bordered
                style={{ width: '100%' }}
                expandIconPosition="end"
              />
            </div>
          </div>
        )}
        {activeTabKey === 'past' && selectedEvent && 'job_id' in selectedEvent && (
          <div className="event-detail">
            <div className="title">
              <div>
                <div className="event-title">{selectedEvent && selectedEvent.title}</div>
                <div className="event-time">
                  {selectedEvent &&
                    'start_time' in selectedEvent &&
                    formatEventDate(
                      selectedEvent.start_time,
                      'end_time' in selectedEvent ? selectedEvent.end_time : null,
                    )}
                </div>
              </div>
              <Button
                type="primary"
                className="meeting-recording-download-button"
                aria-label="Download recording"
                icon={<DownloadOutlined />}
                loading={downloadLoading}
                onClick={downloadRecord}
              >
                {'Download recording'}
              </Button>
            </div>
            {selectedEvent && 'url' in selectedEvent
              ? renderMeetingInfo(selectedEvent, selectedEvent.url)
              : null}
            <div className="content">
              <Collapse
                items={pastCollapseItems}
                activeKey={pastOpenPanelKeys}
                onChange={(keys) => setPastOpenPanelKeys(collapseKeysFromChange(keys))}
                expandIcon={renderCollapseIcon}
                bordered
                style={{ width: '100%' }}
                expandIconPosition="end"
              />
            </div>
          </div>
        )}
        {activeTabKey === 'settings' && selectedAccount && (
          <div className="account-detail">
            <div className="title">My calendars</div>
            <List
              dataSource={selectedAccount.calendars}
              renderItem={(calendar) => (
                <List.Item>
                  <Checkbox
                    checked={calendar.subscribed}
                    onChange={(e) => {
                      const data = {
                        op: 'replace',
                        calendar_id: calendar.id,
                        field: 'enabled',
                        value: e.target.checked,
                      };
                      updateGoogleCalendars(selectedAccount.email, {
                        patches: [data],
                      });
                      refreshAccounts();
                    }}
                  >
                    {calendar.title}
                  </Checkbox>
                </List.Item>
              )}
            />
          </div>
        )}
        {activeTabKey === 'add-meeting' && (
          <div className="event-detail add-meeting-form">
            <div className="title">
              <div className="event-title">Add to Existing Meeting</div>
            </div>
            <div className="content">
              <Form layout="vertical" onFinish={handleFormSubmit}>
                <Form.Item
                  label="Meeting name"
                  name="meetingName"
                  rules={[
                    {
                      required: true,
                      message: 'Please input the meeting name!',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Meeting URL"
                  name="meetingUrl"
                  rules={[
                    {
                      required: true,
                      message: 'Please input the meeting URL!',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={startNotetakerLoading}>
                    Start Notetaker
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingHub;
